import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/icons';
import { useAuth } from '@/context/AuthContext';

// Testimonial data
const testimonials = [
  {
    quote: "LeadPilot helped us identify qualified leads we never would have found otherwise. Our sales cycle shortened by 30% since implementing it.",
    author: "Sarah Johnson",
    title: "VP of Sales, TechCorp",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&auto=format"
  },
  {
    quote: "The data enrichment feature alone saved our team countless hours of manual research, and the leads are actually responding to our outreach!",
    author: "Michael Chen",
    title: "Growth Lead, StartupX",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&auto=format"
  },
  {
    quote: "As a small business, we never had access to this quality of leads before. LeadPilot has completely transformed our B2B prospecting strategy.",
    author: "Amanda Rivera",
    title: "Founder, Cloudwise",
    avatar: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=100&h=100&fit=crop&auto=format"
  }
];

const LandingPage = () => {
  const { user } = useAuth();

  // If user is logged in, redirect to the app route
  if (user) {
    return <Navigate to="/app" replace />;
  }

  // Show landing page content for non-logged in users
  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#F8F9FF]">
        <div className="container mx-auto px-4 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block bg-blue-100 text-primary rounded-full px-3 py-1 text-sm font-medium mb-6">Now in Beta ✦</div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Find & Connect with Your <span className="text-primary block">Perfect B2B Leads</span>
            </h1>
            <p className="text-gray-600 mb-8">
              Automatically discover qualified prospects by location and industry, enrich their data, and manage your outreach—all in one powerful platform.
            </p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex">
              <Button asChild size="lg">
                <Link to="/auth">Join Beta</Link>
              </Button>
              <div className="text-sm text-gray-500 mt-2">Free trial available</div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-blue-100">
            <img src="/lovable-uploads/0b72657e-6b88-43a4-9015-09236769f9e6.png" alt="Person typing on laptop keyboard - Discovery Hub platform" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">All-In-One Lead Generation Platform</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Everything you need to find, connect with, and convert your ideal B2B customers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">AI-Powered Lead Discovery</h3>
              <p className="text-gray-600">Find perfect prospects filtered by location, industry, company size, and more with our intelligent search algorithms.</p>
            </div>

            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694-4.125-8.25-4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">Automated Data Enrichment</h3>
              <p className="text-gray-600">Get detailed business information, company insights, and contact profiles automatically appended to your leads.</p>
            </div>

            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm">
              <div className="inline-flex w-12 h-12 items-center justify-center bg-blue-100 rounded-lg text-primary mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">Built-In CRM</h3>
              <p className="text-gray-600">Manage your leads with our intuitive CRM designed specifically for sales prospecting and outreach funnels.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-[#F8F9FF]">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Discovery Hub Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">A simple four-step process to revolutionize your B2B lead generation</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-12">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">01</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Define Your Ideal Customer</h3>
                  <p className="text-gray-600">Set your target industry, location, company size, and other parameters to narrow down your search.</p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">02</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Generate Qualified Leads</h3>
                  <p className="text-gray-600">Our AI engine scans millions of data points to identify and verify the perfect prospects for your business.</p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">03</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Enrich & Prioritize</h3>
                  <p className="text-gray-600">Each lead is enriched with contact details, social profiles, and buying intent signals to help you optimize outreach.</p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">04</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Connect & Convert</h3>
                  <p className="text-gray-600">Use our built-in CRM and email tools to run outbound campaigns and track your sales pipeline.</p>
                </div>
              </div>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-lg border border-blue-100">
              <img src="/lovable-uploads/a205c4d3-ecdc-4809-99ba-3a7777b13819.png" alt="Person typing on laptop keyboard - Discovery Hub platform" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Updated Section */}
      <section className="py-16 gradient-bg text-white bg-primary">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">What Beta Users Are Saying</h2>
            <p className="text-white/80 text-lg">
              Early adopters are already seeing remarkable results with Discovery Hub
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
              >
                <div className="mb-4">
                  <svg className="h-6 w-6 text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18V10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z" />
                  </svg>
                </div>
                <p className="mb-4 text-white/90">{testimonial.quote}</p>
                <div className="flex items-center">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.author}
                    className="h-10 w-10 rounded-full mr-3 object-cover"
                  />
                  <div>
                    <h4 className="font-medium">{testimonial.author}</h4>
                    <p className="text-sm text-white/70">{testimonial.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-3xl mx-auto bg-[#F8F9FF] p-8 md:p-12 rounded-2xl text-center">
            <div className="inline-block bg-amber-100 text-amber-600 rounded-full px-3 py-1 text-sm font-medium mb-6">Limited Time Offer</div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get 50% Off When We Launch</h2>
            <p className="text-gray-600 mb-8">Join our beta waitlist today and receive 50% off your first 6 months when we launch</p>
            
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input type="email" placeholder="Enter your email" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                <Button className="whitespace-nowrap">Join Now</Button>
              </div>
              <p className="text-xs text-gray-500 mt-3">No credit card required. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LandingPage;
